// Complete Fixed BaseLayout.tsx with TypeScript errors resolved
// frontend/src/components/Layout/BaseLayout.tsx

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Menu,
  X,
  LogOut,
  Bell,
  Search,
  ChevronDown,
  Home,
} from 'lucide-react';

export interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  current?: boolean;
  badge?: string;
  children?: NavigationItem[];
  requiredRoles?: string[];
}

export interface LayoutConfig {
  title: string;
  subtitle?: string;
  theme: {
    primary: string;
    secondary: string;
    accent: string;
    sidebar?: string;
    text?: string;
  };
  navigation: NavigationItem[];
  showSearch?: boolean;
  showNotifications?: boolean;
  logo?: React.ReactNode;
  userMenuItems?: Array<{
    name: string;
    href: string;
    icon: React.ComponentType<any>;
  }>;
}

interface BaseLayoutProps {
  children: React.ReactNode;
  config: LayoutConfig;
}

const BaseLayout: React.FC<BaseLayoutProps> = ({ children, config }) => {
  const { user, logout, hasRole } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Filter navigation based on user roles
  const filteredNavigation = config.navigation.filter(item => {
    if (!item.requiredRoles) return true;
    return item.requiredRoles.some(role => hasRole(role));
  });

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile menu */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <SidebarContent config={config} navigation={filteredNavigation} onLogout={handleLogout} />
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <SidebarContent config={config} navigation={filteredNavigation} onLogout={handleLogout} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top nav */}
        <TopNavigation config={config} onMenuClick={() => setSidebarOpen(true)} />

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// Enhanced SidebarContent with better theming
interface SidebarContentProps {
  config: LayoutConfig;
  navigation: NavigationItem[];
  onLogout: () => void;
}

const SidebarContent: React.FC<SidebarContentProps> = ({ config, navigation, onLogout }) => {
  const { user } = useAuth();

  return (
    <div className="flex flex-col h-0 flex-1">
      {/* Logo */}
      <div className={`flex items-center h-16 flex-shrink-0 px-4 ${config.theme.primary}`}>
        <div className="flex items-center">
          <div className="flex-shrink-0">
            {config.logo || (
              <div className="h-8 w-8 bg-white rounded-lg flex items-center justify-center">
                <Home className={`h-5 w-5 ${config.theme.accent}`} />
              </div>
            )}
          </div>
          <div className="ml-3">
            <h1 className={`text-lg font-semibold ${config.theme.text || 'text-white'}`}>
              {config.title}
            </h1>
            {config.subtitle && (
              <p className="text-xs text-white opacity-80">
                {config.subtitle}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className={`flex-1 flex flex-col overflow-y-auto ${config.theme.sidebar || 'bg-white'}`}>
        <nav className="flex-1 px-2 py-4 space-y-1">
          {navigation.map((item) => (
            <NavigationLink key={item.name} item={item} theme={config.theme} />
          ))}
        </nav>

        {/* User info */}
        <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
          <div className="flex items-center w-full">
            <div>
              <img
                className="inline-block h-9 w-9 rounded-full"
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.username || 'User')}&background=2563eb&color=ffffff`}
                alt={user?.username}
              />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-700">{user?.username}</p>
              <p className="text-xs font-medium text-gray-500 capitalize">{user?.role}</p>
            </div>
            <button
              onClick={onLogout}
              className="flex-shrink-0 bg-white p-1 text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Logout"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// FIXED NavigationLink with proper TypeScript handling
interface NavigationLinkProps {
  item: NavigationItem;
  theme: LayoutConfig['theme'];
}

const NavigationLink: React.FC<NavigationLinkProps> = ({ item, theme }) => {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const isActive = location.pathname === item.href;
  // FIXED: Explicit boolean conversion to prevent TypeScript errors
  const hasActiveChild = Boolean(item.children?.some(child => location.pathname === child.href));
  const shouldExpand = hasActiveChild || isExpanded;

  // Theme-aware styles
  const getActiveStyles = () => {
    if (theme.accent.includes('blue')) return 'bg-blue-100 text-blue-900';
    if (theme.accent.includes('green')) return 'bg-green-100 text-green-900';
    if (theme.accent.includes('purple')) return 'bg-purple-100 text-purple-900';
    return 'bg-blue-100 text-blue-900';
  };

  const getIconStyles = (active: boolean) => {
    if (active) {
      if (theme.accent.includes('blue')) return 'text-blue-500';
      if (theme.accent.includes('green')) return 'text-green-500';
      if (theme.accent.includes('purple')) return 'text-purple-500';
      return 'text-blue-500';
    }
    return 'text-gray-400 group-hover:text-gray-500';
  };

  const getBadgeStyles = () => {
    if (theme.primary.includes('blue')) return 'bg-blue-600';
    if (theme.primary.includes('green')) return 'bg-green-600';
    if (theme.primary.includes('purple')) return 'bg-purple-600';
    return 'bg-blue-600';
  };

  if (item.children && item.children.length > 0) {
    return (
      <div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md transition-colors ${
            hasActiveChild
              ? getActiveStyles()
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <item.icon className={`mr-3 h-5 w-5 ${getIconStyles(hasActiveChild)}`} />
          {item.name}
          {item.badge && (
            <span className={`ml-2 ${getBadgeStyles()} text-white text-xs px-2 py-0.5 rounded-full`}>
              {item.badge}
            </span>
          )}
          <ChevronDown
            className={`ml-auto h-4 w-4 transition-transform ${
              shouldExpand ? 'transform rotate-180' : ''
            }`}
          />
        </button>
        {shouldExpand && (
          <div className="ml-6 mt-1 space-y-1">
            {item.children.map((child) => (
              <NavigationLink key={child.name} item={child} theme={theme} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.href}
      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
        isActive
          ? getActiveStyles()
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      }`}
    >
      <item.icon className={`mr-3 h-5 w-5 ${getIconStyles(isActive)}`} />
      {item.name}
      {item.badge && (
        <span className={`ml-auto ${getBadgeStyles()} text-white text-xs px-2 py-0.5 rounded-full`}>
          {item.badge}
        </span>
      )}
    </Link>
  );
};

// Enhanced TopNavigation
interface TopNavigationProps {
  config: LayoutConfig;
  onMenuClick: () => void;
}

const TopNavigation: React.FC<TopNavigationProps> = ({ config, onMenuClick }) => {
  const { user } = useAuth();
  const location = useLocation();

  const getCurrentPageTitle = () => {
    const findCurrentPage = (navItems: NavigationItem[]): string => {
      for (const item of navItems) {
        if (item.href === location.pathname) {
          return item.name;
        }
        if (item.children) {
          const childTitle = findCurrentPage(item.children);
          if (childTitle) return childTitle;
        }
      }
      return '';
    };

    return findCurrentPage(config.navigation) || 'Dashboard';
  };

  return (
    <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
      <button
        className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden"
        onClick={onMenuClick}
      >
        <Menu className="h-6 w-6" />
      </button>
      
      <div className="flex-1 px-4 flex justify-between items-center">
        <div className="flex items-center">
          <h1 className="text-xl font-semibold text-gray-900">{getCurrentPageTitle()}</h1>
        </div>

        {config.showSearch && (
          <div className="flex-1 max-w-lg mx-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none pl-3">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search..."
                type="search"
              />
            </div>
          </div>
        )}
        
        <div className="flex items-center space-x-4">
          {config.showNotifications && (
            <button className="bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              <Bell className="h-6 w-6" />
            </button>
          )}

          <div className="flex items-center">
            <img
              className="h-8 w-8 rounded-full"
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.username || 'User')}&background=2563eb&color=ffffff`}
              alt={user?.username}
            />
            <div className="ml-3 hidden sm:block">
              <span className="text-gray-700 text-sm font-medium">{user?.username}</span>
              <span className="ml-2 text-xs text-gray-500 capitalize">({user?.role})</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaseLayout;